/* Complete the Stack — quick-add microinteraction + GA4 tracking
 *
 * Quick-add: listens for [data-aios-cts-add] click, POSTs /cart/add.js with
 *   hidden line item properties (`_cts_source_handle`, `_cts_rank`, `_cts_rule`)
 *   so order webhooks carry deterministic CTS attribution. Underscore prefix
 *   keeps them out of the customer-facing order email. Same pattern Rebuy uses.
 *
 * Tracking: emits GA4 ecommerce events on every interaction.
 *   - Section enters viewport ≥50% for ≥200ms → `view_item_list` (impression — fires once per PDP visit)
 *   - Card link click → `select_item` (PDP click — soft signal)
 *   - Quick-add click → `add_to_cart` (cart add — purchase intent signal)
 *   Each event carries: source PDP handle/id/title, candidate handle/id/title,
 *   rank (1-3), rule (co_purchase / cheap_addon / best_seller_anchor / ...),
 *   price. Source PDP context is read from the section's data-cts-source-*
 *   attributes; candidate context from the card's data-cts-* attributes.
 *
 * Dispatch: publishes to Shopify.analytics → custom pixel forwards to GA4 via
 *   gtag. Also pushes to window.dataLayer + window.gtag as fallbacks.
 *
 * Multi-variant products in the section render as plain links (no quick-add)
 *   — clicking them sends the customer to the PDP to choose options.
 */
(function () {
  'use strict';

  var BUTTON_RESET_MS = 1500;
  var ITEM_LIST_ID = 'complete_the_stack';
  var ITEM_LIST_NAME = 'Complete the Stack';
  var IMPRESSION_THRESHOLD = 0.5;
  var IMPRESSION_DWELL_MS = 200;
  // Shopify's Custom Web Pixel iframe bootstraps async — any publish() that
  // lands before the pixel finishes subscribing is silently dropped (Shopify
  // does not replay). We gate publishes on the earliest of two signals:
  //   (a) window.load + POST_LOAD_BUFFER_MS  — correct signal, guaranteed
  //       to fire after every subresource (including the pixel iframe) loads
  //   (b) script start + READINESS_CAP_MS    — fallback cap so a page that
  //       never finishes loading (background polling, lazy resources) doesn't
  //       keep our queue pending forever
  // See memory reference_shopify_pixel_subscription_quirks.md (16/05/26).
  var POST_LOAD_BUFFER_MS = 500;
  var READINESS_CAP_MS = 8000;
  var pendingPublishes = [];
  var pixelLikelyReady = false;

  function getSectionContext(el) {
    var section = el && el.closest ? el.closest('[data-aios-cts]') : null;
    if (!section) return {};
    return {
      source_handle: section.getAttribute('data-cts-source-handle') || '',
      source_id: section.getAttribute('data-cts-source-id') || '',
      source_title: section.getAttribute('data-cts-source-title') || ''
    };
  }

  function getCardContext(el) {
    var card = el && el.closest ? el.closest('[data-aios-cts-card]') : null;
    if (!card) return {};
    var priceCents = parseInt(card.getAttribute('data-cts-price') || '0', 10);
    return {
      handle: card.getAttribute('data-cts-handle') || '',
      product_id: card.getAttribute('data-cts-product-id') || '',
      title: card.getAttribute('data-cts-title') || '',
      rank: parseInt(card.getAttribute('data-cts-rank') || '0', 10),
      rule: card.getAttribute('data-cts-rule') || '',
      price: isNaN(priceCents) ? 0 : priceCents / 100
    };
  }

  function buildItem(card, src) {
    return {
      item_id: card.handle,
      item_name: card.title,
      item_list_id: ITEM_LIST_ID,
      item_list_name: ITEM_LIST_NAME,
      index: card.rank,
      price: card.price,
      quantity: 1,
      // Custom params for attributing back to the source PDP. GA4 keeps these
      // on the event as custom parameters; create matching custom dimensions
      // in GA4 to surface them in reports.
      cts_rule: card.rule,
      cts_rank: card.rank,
      cts_source_handle: src.source_handle,
      cts_source_id: src.source_id,
      cts_source_title: src.source_title
    };
  }

  function publishToShopify(shopifyEventName, payload) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(shopifyEventName, payload);
      }
    } catch (e) { /* non-fatal */ }
  }

  function markPixelReady() {
    if (pixelLikelyReady) return;
    pixelLikelyReady = true;
    var queue = pendingPublishes.slice();
    pendingPublishes.length = 0;
    for (var i = 0; i < queue.length; i++) {
      publishToShopify(queue[i].event, queue[i].payload);
    }
  }

  function armPixelReadiness() {
    // Primary signal: the pixel sandbox posts a message to the parent when it
    // finishes registering subscriptions. Deterministic — no timing guess.
    window.addEventListener('message', function (e) {
      if (e && e.data && e.data.source === 'aios-cts-pixel' && e.data.type === 'aios_cts_pixel_ready') {
        markPixelReady();
      }
    });
    // Fallback 1: window.load + buffer (in case the sandbox CSP blocks
    // parent.postMessage). Still correct — pixel iframe loads before load.
    if (document.readyState === 'complete') {
      setTimeout(markPixelReady, POST_LOAD_BUFFER_MS);
    } else {
      window.addEventListener('load', function () {
        setTimeout(markPixelReady, POST_LOAD_BUFFER_MS);
      });
    }
    // Fallback 2: hard cap so a page that never fires window.load (background
    // polling, lazy resources) doesn't keep our queue pending forever.
    setTimeout(markPixelReady, READINESS_CAP_MS);
  }
  armPixelReadiness();

  function schedulePixelPublish(shopifyEventName, payload) {
    if (pixelLikelyReady) {
      publishToShopify(shopifyEventName, payload);
      return;
    }
    pendingPublishes.push({ event: shopifyEventName, payload: payload });
  }

  function dispatch(gaEventName, shopifyEventName, payload) {
    // Primary path: Shopify analytics bridge → custom Web Pixel → GA4.
    // This is the only path that actually reaches GA4 on a Shopify store
    // where GA4 is wired via the Google & YouTube channel pixel (sandboxed).
    // Gated behind window.load + POST_LOAD_BUFFER_MS so early auto-fires wait for the pixel.
    schedulePixelPublish(shopifyEventName, payload);
    // Secondary fallbacks (no-op if GTM/gtag not in parent page):
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: gaEventName }, payload));
    } catch (e) { /* non-fatal */ }
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', gaEventName, payload);
      }
    } catch (e) { /* non-fatal */ }
  }

  function emitGAEvent(eventName, src, card) {
    if (!card.handle) return;
    var payload = {
      item_list_id: ITEM_LIST_ID,
      item_list_name: ITEM_LIST_NAME,
      items: [buildItem(card, src)],
      cts_source_handle: src.source_handle,
      cts_source_id: src.source_id,
      cts_source_title: src.source_title,
      cts_rule: card.rule,
      cts_rank: card.rank
    };
    if (eventName === 'add_to_cart') {
      payload.value = card.price;
      payload.currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
    }
    var shopifyEventName = eventName === 'add_to_cart' ? 'aios_cts_add' : 'aios_cts_select';
    dispatch(eventName, shopifyEventName, payload);
  }

  function emitImpression(section) {
    var src = {
      source_handle: section.getAttribute('data-cts-source-handle') || '',
      source_id: section.getAttribute('data-cts-source-id') || '',
      source_title: section.getAttribute('data-cts-source-title') || ''
    };
    var cards = section.querySelectorAll('[data-aios-cts-card]');
    var items = [];
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var priceCents = parseInt(card.getAttribute('data-cts-price') || '0', 10);
      var ctx = {
        handle: card.getAttribute('data-cts-handle') || '',
        title: card.getAttribute('data-cts-title') || '',
        rank: parseInt(card.getAttribute('data-cts-rank') || '0', 10),
        rule: card.getAttribute('data-cts-rule') || '',
        price: isNaN(priceCents) ? 0 : priceCents / 100
      };
      if (ctx.handle) items.push(buildItem(ctx, src));
    }
    if (!items.length) return;
    var payload = {
      item_list_id: ITEM_LIST_ID,
      item_list_name: ITEM_LIST_NAME,
      items: items,
      cts_source_handle: src.source_handle,
      cts_source_id: src.source_id,
      cts_source_title: src.source_title
    };
    dispatch('view_item_list', 'aios_cts_view_list', payload);
  }

  // Fire view_item_list once per PDP visit when the CTS section enters the
  // viewport ≥IMPRESSION_THRESHOLD for ≥IMPRESSION_DWELL_MS. Pattern matches
  // Littledata's published spec (80%/200ms/500ms) — slightly looser threshold
  // because the cross-sell strip is a single horizontal block, not a grid.
  function watchSectionImpression(section) {
    if (section.dataset.ctsImpressionFired === '1') return;
    if (typeof window.IntersectionObserver !== 'function') {
      // Old browsers — fall back to immediate fire on next tick.
      setTimeout(function () {
        if (section.dataset.ctsImpressionFired === '1') return;
        section.dataset.ctsImpressionFired = '1';
        emitImpression(section);
      }, 500);
      return;
    }
    var dwellTimer = null;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && entry.intersectionRatio >= IMPRESSION_THRESHOLD) {
          if (dwellTimer) return;
          dwellTimer = setTimeout(function () {
            if (section.dataset.ctsImpressionFired === '1') return;
            section.dataset.ctsImpressionFired = '1';
            emitImpression(section);
            observer.unobserve(section);
          }, IMPRESSION_DWELL_MS);
        } else if (dwellTimer) {
          clearTimeout(dwellTimer);
          dwellTimer = null;
        }
      });
    }, { threshold: [IMPRESSION_THRESHOLD] });
    observer.observe(section);
  }

  function initImpressions() {
    var sections = document.querySelectorAll('[data-aios-cts]');
    sections.forEach(watchSectionImpression);
  }

  function bumpCartBubble() {
    var bubbles = document.querySelectorAll('.cart-count-bubble');
    bubbles.forEach(function (bubble) {
      bubble.style.display = '';
      var span = bubble.querySelector('span[aria-hidden="true"]') || bubble.querySelector('span:not(.visually-hidden)');
      if (span) {
        var current = parseInt(span.textContent.trim(), 10);
        if (isNaN(current)) current = 0;
        span.textContent = current + 1;
      } else if (!bubble.textContent.trim()) {
        bubble.textContent = '1';
      }
    });
  }

  function refreshCartDrawer() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
        var drawer = document.querySelector('cart-drawer, cart-notification');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents({ items: cart.items, item_count: cart.item_count });
        }
      })
      .catch(function () { /* non-fatal */ });
  }

  function flashAdded(button) {
    var icon = button.querySelector('[data-aios-cts-icon]');
    button.setAttribute('data-cts-added', '1');
    if (icon) icon.textContent = '✓';
    setTimeout(function () {
      button.removeAttribute('data-cts-added');
      if (icon) icon.textContent = '+';
    }, BUTTON_RESET_MS);
  }

  function flashError(button) {
    var icon = button.querySelector('[data-aios-cts-icon]');
    if (icon) icon.textContent = '!';
    setTimeout(function () {
      if (icon) icon.textContent = '+';
    }, BUTTON_RESET_MS);
  }

  // Track card-link clicks (PDP navigations). Fires before the browser
  // follows the link — the click bubbles up to document, we tag the event,
  // then default behaviour proceeds. Don't preventDefault.
  document.addEventListener('click', function (e) {
    var link = e.target.closest('.aios-cts__link, .aios-cts-block__row-link, .aios-cts__add--options, .aios-cts-block__add--options');
    if (!link) return;
    if (!link.closest('[data-aios-cts]')) return;
    var src = getSectionContext(link);
    var card = getCardContext(link);
    if (!card.handle) return;
    emitGAEvent('select_item', src, card);
  }, true);

  document.addEventListener('click', function (e) {
    var button = e.target.closest('[data-aios-cts-add]');
    if (!button) return;
    if (button.getAttribute('data-cts-pending') === '1') return;
    e.preventDefault();

    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    var src = getSectionContext(button);
    var card = getCardContext(button);
    emitGAEvent('add_to_cart', src, card);

    button.setAttribute('data-cts-pending', '1');

    // Hidden line item properties — underscore prefix keeps them out of the
    // customer-facing order email but they persist into the order webhook so
    // the dashboard can deterministically attribute revenue back to a CTS
    // click. Same mechanism Rebuy uses.
    var safeRank = (card.rank && !isNaN(card.rank)) ? String(card.rank) : '';
    var lineItem = {
      id: parseInt(variantId, 10),
      quantity: 1,
      properties: {
        _cts_source_handle: src.source_handle || '',
        _cts_rank: safeRank,
        _cts_rule: card.rule || ''
      }
    };

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: [lineItem] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart_add_failed_' + r.status);
        return r.json();
      })
      .then(function () {
        flashAdded(button);
        bumpCartBubble();
        refreshCartDrawer();
      })
      .catch(function (err) {
        console.warn('[aios-cts] add to cart failed', err);
        flashError(button);
      })
      .finally(function () {
        button.removeAttribute('data-cts-pending');
      });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initImpressions);
  } else {
    initImpressions();
  }
})();
